import { useState, useEffect, useCallback } from 'react';
import {
  QrCode,
  Smartphone,
  CircleCheck as CheckCircle2,
  RefreshCw,
  ShieldCheck,
  MessageCircle,
  Wallet,
  Users,
  LoaderCircle as Loader2,
  CircleAlert as AlertCircle,
  Link2,
  Sparkles,
  LogOut,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  waCreateInstance,
  waStatus,
  waRefreshQr,
  waDisconnect,
} from '../lib/functions';

/* ------------------------------------------------------------------ */
/* helpers                                                            */
/* ------------------------------------------------------------------ */

// The QR may arrive as a full data-URI or a raw base64 PNG.
const toQrSrc = (qr) => {
  if (!qr) return '';
  return qr.startsWith('data:') ? qr : `data:image/png;base64,${qr}`;
};

const statusOf = (res) => res?.status || res?.state?.status || res?.state || '';

// Try hard to surface the owner's number from an assortment of shapes.
const extractNumber = (res) => {
  const raw =
    res?.number ||
    res?.phone ||
    res?.me ||
    res?.owner ||
    res?.instance?.number ||
    res?.state?.number ||
    '';
  if (!raw || typeof raw !== 'string') return '';
  // e.g. "972501234567@s.whatsapp.net" -> "+972 50-123-4567"
  const digits = raw.split('@')[0].replace(/\D/g, '');
  if (!digits) return '';
  return `+${digits}`;
};

/* ------------------------------------------------------------------ */
/* small presentational pieces                                        */
/* ------------------------------------------------------------------ */

const HowItWorks = () => {
  const steps = [
    {
      Icon: MessageCircle,
      title: 'שחקנים שולחים הודעה',
      body: 'המשתתפים כותבים למספר שלכם בוואטסאפ — בלי אפליקציה, בלי הרשמה.',
    },
    {
      Icon: Wallet,
      title: 'הסוכן החכם מטפל בהכל',
      body: 'רושם משתתפים, עוקב אחרי כניסות ויציאות, ומנהל את רשימת ההמתנה אוטומטית.',
    },
    {
      Icon: ShieldCheck,
      title: 'אתם תמיד בשליטה',
      body: 'כל שינוי נשמר באפליקציה. אפשר לנהל מהמסך או ישירות מהוואטסאפ.',
    },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      {steps.map((step, i) => (
        <div
          key={i}
          className="card-premium hover-lift p-5"
          style={{ animationDelay: `${i * 120}ms` }}
        >
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-felt-500/12 border border-felt-500/30">
            <step.Icon className="h-5 w-5 text-felt-400" />
          </div>
          <h4 className="mb-1 text-base text-slate-100">{step.title}</h4>
          <p className="text-sm leading-relaxed text-slate-400">{step.body}</p>
        </div>
      ))}
    </div>
  );
};

const ErrorChip = ({ children }) =>
  children ? (
    <div className="mt-4 inline-flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-200">
      <AlertCircle className="h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  ) : null;

/* ------------------------------------------------------------------ */
/* main component                                                     */
/* ------------------------------------------------------------------ */

export const WhatsAppLink = () => {
  const { direction } = useLanguage();
  const isRtl = direction === 'rtl';

  // phase: 'checking' | 'idle' | 'qr' | 'connected'
  const [phase, setPhase] = useState('checking');
  const [qr, setQr] = useState('');
  const [pairingCode, setPairingCode] = useState('');
  const [number, setNumber] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  const markConnected = useCallback((res) => {
    setNumber(extractNumber(res));
    setQr('');
    setPairingCode('');
    setPhase('connected');
    setCelebrate(true);
    setTimeout(() => setCelebrate(false), 2600);
  }, []);

  /* initial status check ------------------------------------------- */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await waStatus();
        if (!alive) return;
        if (statusOf(res) === 'connected') {
          setNumber(extractNumber(res));
          setPhase('connected');
        } else {
          setPhase('idle');
        }
      } catch {
        if (!alive) return;
        setError('לא הצלחנו לבדוק את מצב החיבור. נסו שוב.');
        setPhase('idle');
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /* poll while a QR is showing ------------------------------------- */
  useEffect(() => {
    if (phase !== 'qr') return undefined;
    const id = setInterval(async () => {
      try {
        const res = await waStatus();
        if (statusOf(res) === 'connected') {
          clearInterval(id);
          markConnected(res);
        }
      } catch {
        /* transient — keep polling */
      }
    }, 3000);
    return () => clearInterval(id);
  }, [phase, markConnected]);

  /* actions --------------------------------------------------------- */
  const handleConnect = async () => {
    setError('');
    setCreating(true);
    try {
      const res = await waCreateInstance();
      setQr(toQrSrc(res?.qr));
      setPairingCode(res?.pairingCode || '');
      setPhase('qr');
    } catch {
      setError('יצירת קוד ה-QR נכשלה. נסו שוב בעוד רגע.');
    } finally {
      setCreating(false);
    }
  };

  const handleRefresh = async () => {
    setError('');
    setRefreshing(true);
    try {
      const res = await waRefreshQr();
      if (statusOf(res) === 'connected') {
        markConnected(res);
      } else {
        setQr(toQrSrc(res?.qr));
        setPairingCode(res?.pairingCode || '');
      }
    } catch {
      setError('רענון הקוד נכשל. נסו שוב.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDisconnect = async () => {
    setError('');
    setDisconnecting(true);
    try {
      await waDisconnect();
      setNumber('');
      setQr('');
      setPairingCode('');
      setPhase('idle');
    } catch {
      setError('הניתוק נכשל. נסו שוב.');
    } finally {
      setDisconnecting(false);
    }
  };

  /* ---------------------------------------------------------------- */
  return (
    <div dir={isRtl ? 'rtl' : 'ltr'} className="min-h-screen w-full px-4 py-10 sm:py-14">
      <div className="mx-auto w-full max-w-3xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-felt-500/30 bg-felt-500/10 px-4 py-1.5 text-xs font-semibold text-felt-400">
            <Sparkles className="h-3.5 w-3.5" />
            סוכן וואטסאפ חכם
          </div>
          <h1 className="text-3xl font-display font-bold tracking-tight sm:text-4xl">
            <span className="text-felt-gradient">חברו את הוואטסאפ שלכם</span>
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-slate-400">
            תנו לשחקנים לשריין מקום, לעדכן כניסות ולנהל את הרשימה — הכל דרך
            הודעה פשוטה בוואטסאפ, בזמן שאתם נשארים בשליטה מלאה.
          </p>
        </div>

        {/* ---- checking ---- */}
        {phase === 'checking' && (
          <div className="card-premium flex flex-col items-center justify-center gap-4 p-14">
            <Loader2 className="h-8 w-8 animate-spin text-felt-400" />
            <p className="text-sm text-slate-400">בודקים את מצב החיבור…</p>
          </div>
        )}

        {/* ---- connected ---- */}
        {phase === 'connected' && (
          <div
            className={`card-premium relative overflow-hidden p-8 text-center sm:p-12 ${
              celebrate ? 'animate-pulseGlow' : ''
            }`}
            style={{
              background:
                'linear-gradient(180deg, rgba(12,127,78,0.22) 0%, rgba(11,15,26,0.94) 70%)',
              borderColor: 'rgba(47,190,130,0.35)',
            }}
          >
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-40 opacity-60"
              style={{
                background:
                  'radial-gradient(420px 180px at 50% -20%, rgba(47,190,130,0.35), transparent 70%)',
              }}
            />
            <div className="relative">
              <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full border border-felt-400/40 bg-felt-500/15 shadow-felt animate-floaty">
                <CheckCircle2 className="h-10 w-10 text-felt-400" />
              </div>
              <h2 className="text-2xl font-display font-bold text-slate-50 sm:text-3xl">
                הוואטסאפ מחובר 🎉
              </h2>
              {number ? (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-felt-500/30 bg-felt-500/10 px-4 py-1.5">
                  <Smartphone className="h-4 w-4 text-felt-400" />
                  <span dir="ltr" className="tabular-nums text-sm font-semibold text-felt-50">
                    {number}
                  </span>
                </div>
              ) : (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-felt-500/30 bg-felt-500/10 px-4 py-1.5 text-sm font-semibold text-felt-50">
                  <span className="h-2 w-2 rounded-full bg-felt-400 animate-pulseGlow" />
                  פעיל ומחובר
                </div>
              )}
              <p className="mx-auto mt-5 max-w-md text-base leading-relaxed text-slate-300">
                מעכשיו שחקנים יכולים לשלוח הודעה למספר שלכם — הסוכן ירשום אותם,
                יעקוב אחרי הכניסות והיציאות וינהל את רשימת ההמתנה בשבילכם.
              </p>

              <div className="mx-auto mt-6 max-w-md rounded-2xl border border-gold-500/25 bg-gold-500/[0.06] p-5 text-right">
                <div className="mb-2 flex items-center gap-2 text-gold-300">
                  <Sparkles className="h-4 w-4" />
                  <span className="text-sm font-semibold">שלטו במשחק מהוואטסאפ</span>
                </div>
                <p className="text-sm leading-relaxed text-slate-300">
                  שלחו הודעה לעצמכם (הצ׳אט <span className="text-slate-100">"הודעה לעצמי"</span>) —
                  הסוכן מזהה שזה אתם ומבצע כל פקודה:
                </p>
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  {['תתחיל את המשחק', 'מי רשום ומה בקופה?', 'תוסיף את דוד 054… עם 500', 'תסגור את המשחק'].map(
                    (ex) => (
                      <span
                        key={ex}
                        dir="rtl"
                        className="rounded-lg border border-white/10 bg-ink-800/60 px-2.5 py-1 text-xs text-slate-300"
                      >
                        {ex}
                      </span>
                    ),
                  )}
                </div>
              </div>

              <div className="mx-auto mt-7 max-w-sm">
                <button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="btn-ghost w-full"
                >
                  {disconnecting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LogOut className="h-4 w-4" />
                  )}
                  {disconnecting ? 'מנתקים…' : 'נתקו את הוואטסאפ'}
                </button>
              </div>
              <ErrorChip>{error}</ErrorChip>
            </div>
          </div>
        )}

        {/* ---- idle (not connected, no QR yet) ---- */}
        {phase === 'idle' && (
          <div className="card-premium relative overflow-hidden p-8 text-center sm:p-12">
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-44 opacity-70"
              style={{
                background:
                  'radial-gradient(460px 190px at 50% -30%, rgba(212,175,55,0.18), transparent 70%)',
              }}
            />
            <div className="relative">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl border border-white/10 bg-white/5 animate-floaty">
                <QrCode className="h-10 w-10 text-gold-400" />
              </div>
              <h2 className="text-2xl font-display font-bold text-slate-50">
                חיבור בסריקה אחת
              </h2>
              <p className="mx-auto mt-3 max-w-md text-base leading-relaxed text-slate-400">
                צרו קוד QR וסרקו אותו מהוואטסאפ שלכם. תוך שניות המספר שלכם הופך
                לעוזר אישי חכם למשחק.
              </p>
              <div className="mx-auto mt-7 max-w-sm">
                <button
                  onClick={handleConnect}
                  disabled={creating}
                  className="btn-gold w-full text-base"
                >
                  {creating ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Link2 className="h-5 w-5" />
                  )}
                  {creating ? 'יוצרים קוד…' : 'צרו קוד QR וחברו וואטסאפ'}
                </button>
              </div>
              <ErrorChip>{error}</ErrorChip>
            </div>
          </div>
        )}

        {/* ---- qr showing ---- */}
        {phase === 'qr' && (
          <div className="card-premium p-6 sm:p-8">
            <div className="grid items-center gap-8 md:grid-cols-2">
              {/* QR frame */}
              <div className="flex flex-col items-center">
                <div className="relative rounded-2xl bg-white p-4 shadow-glow">
                  {qr ? (
                    <img
                      src={qr}
                      alt="WhatsApp QR"
                      className="h-56 w-56 rounded-lg object-contain sm:h-64 sm:w-64"
                    />
                  ) : (
                    <div className="flex h-56 w-56 items-center justify-center sm:h-64 sm:w-64">
                      <Loader2 className="h-8 w-8 animate-spin text-ink-800" />
                    </div>
                  )}
                </div>

                {pairingCode && (
                  <div className="mt-4 text-center">
                    <p className="text-xs text-slate-500">או הזינו קוד צימוד</p>
                    <div
                      dir="ltr"
                      className="mt-1 inline-block rounded-lg border border-white/10 bg-ink-800/70 px-4 py-2 font-mono text-lg font-bold tracking-[0.35em] text-gold-300 tabular-nums"
                    >
                      {pairingCode}
                    </div>
                  </div>
                )}

                <div className="mt-4 inline-flex items-center gap-2 text-sm text-slate-400">
                  <span className="h-2 w-2 rounded-full bg-felt-400 animate-pulseGlow" />
                  ממתינים לחיבור…
                </div>
              </div>

              {/* instructions */}
              <div>
                <h3 className="mb-4 text-lg text-slate-100">איך סורקים?</h3>
                <ol className="space-y-3">
                  {[
                    'פתחו את וואטסאפ בטלפון שלכם',
                    'גשו להגדרות ← מכשירים מקושרים',
                    'הקישו על "קישור מכשיר"',
                    'סרקו את קוד ה-QR שמופיע כאן',
                  ].map((line, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-felt-500/15 border border-felt-500/30 text-xs font-bold text-felt-400 tabular-nums">
                        {i + 1}
                      </span>
                      <span className="text-sm leading-relaxed text-slate-300">
                        {line}
                      </span>
                    </li>
                  ))}
                </ol>

                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="btn-ghost mt-6 w-full"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`}
                  />
                  רעננו קוד
                </button>
                <p className="mt-2 text-center text-xs text-slate-500">
                  הקוד מתחדש כל כמה דקות מטעמי אבטחה.
                </p>
                <ErrorChip>{error}</ErrorChip>
              </div>
            </div>
          </div>
        )}

        {/* How it works */}
        {phase !== 'checking' && (
          <div className="mt-10">
            <div className="mb-4 flex items-center gap-2 text-slate-300">
              <Users className="h-4 w-4 text-gold-400" />
              <h3 className="text-base">איך זה עובד</h3>
            </div>
            <HowItWorks />
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppLink;
